import { useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  applyPrimaryCommunicationPrefs,
  getPrimaryContactRequiredFieldsMessage,
  isPrimaryContactComplete,
} from "utils/contactPrimaryUtils";

function AddContactDialog({ open, onClose, onSave, allContacts }) {
  const [newContact, setNewContact] = useState({
    name: "",
    telephone: null,
    whatsapp: null,
    email: null,
    primary: "no",
    get_email: "no",
    get_whatsapp: "no",
    get_billing: "no",
    get_notification: "no",
  });
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    const value = event.target.value.trim();
    setNewContact({ ...newContact, [field]: value || null });
  };

  const handleNotificationChange = (field) => (event) => {
    const newValue = event.target.checked ? "yes" : "no";

    if (newValue === "no") {
      const hasOtherYes = allContacts.some((c) => c[field] === "yes");
      if (!hasOtherYes) {
        setError(
          `Cannot set ${field.replace(
            "get_",
            ""
          )} to No. At least one contact must receive ${field.replace("get_", "")}.`
        );
        return;
      }
    }

    setNewContact({ ...newContact, [field]: newValue });
    setError(null);
  };

  const handlePrimaryChange = (event) => {
    const newValue = event.target.checked;
    if (newValue) {
      if (!isPrimaryContactComplete(newContact)) {
        setError(getPrimaryContactRequiredFieldsMessage());
        return;
      }
      setNewContact(applyPrimaryCommunicationPrefs({ ...newContact, primary: "yes" }));
      setError(null);
      return;
    }
    if (allContacts.length === 0) {
      setError("Cannot set as not primary. At least one contact must be primary.");
      return;
    }
    setNewContact({ ...newContact, primary: "no" });
  };

  const handleSave = () => {
    if (newContact.primary === "yes" && !isPrimaryContactComplete(newContact)) {
      setError(getPrimaryContactRequiredFieldsMessage());
      return;
    }
    onSave(newContact);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Contact</DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
          <TextField label="Name" value={newContact.name} onChange={handleChange("name")} fullWidth />
          <TextField
            label="Telephone"
            value={newContact.telephone || ""}
            onChange={handleChange("telephone")}
            fullWidth
          />
          <TextField
            label="WhatsApp"
            value={newContact.whatsapp || ""}
            onChange={handleChange("whatsapp")}
            fullWidth
          />
          <TextField label="Email" value={newContact.email || ""} onChange={handleChange("email")} fullWidth />
          {error ? (
            <MDTypography color="error" variant="caption" display="block" sx={{ mt: -0.5 }}>
              {error}
            </MDTypography>
          ) : null}
          <FormControlLabel
            control={<Checkbox checked={newContact.primary === "yes"} onChange={handlePrimaryChange} />}
            label="Primary Contact"
          />
          <FormControlLabel
            control={
              <Checkbox checked={newContact.get_email === "yes"} onChange={handleNotificationChange("get_email")} />
            }
            label="Receive Email"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newContact.get_whatsapp === "yes"}
                onChange={handleNotificationChange("get_whatsapp")}
              />
            }
            label="Receive WhatsApp"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newContact.get_billing === "yes"}
                onChange={handleNotificationChange("get_billing")}
              />
            }
            label="Receive Billing"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newContact.get_notification === "yes"}
                onChange={handleNotificationChange("get_notification")}
              />
            }
            label="Receive Notifications"
          />
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton onClick={handleSave} variant="gradient" color="info">
          Add Contact
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

AddContactDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  allContacts: PropTypes.arrayOf(
    PropTypes.shape({
      customer_contact_id: PropTypes.number,
      name: PropTypes.string,
      telephone: PropTypes.string,
      whatsapp: PropTypes.string,
      email: PropTypes.string,
      primary: PropTypes.string,
      get_email: PropTypes.string,
      get_whatsapp: PropTypes.string,
      get_billing: PropTypes.string,
      get_notification: PropTypes.string,
    })
  ).isRequired,
};

export default AddContactDialog;
